import {
  replacePsycoWithLifestyle,
  SCOPE_LABELS,
} from '@discover-frontend/utils';
import { US_POPULATION_21_PLUS_ID } from '@discover-frontend/constants';

import { alphabeticallySortByKey } from 'src/utils';

interface LevelItem {
  id: string;
  label: string;
  type: string;
  has_children?: boolean;
  parent_id: string;
}

const itemsWithoutAllOption = [
  'demographic',
  'psychographic',
  'lifestyle',
  'personalities',
];

interface DemoValue {
  id: string;
  label: string;
  source: string;
  pic: string;
}
interface DemoItem {
  type: string;
  values: DemoValue[];
}
export const mapDemoItems = (type: string, demos: DemoItem[]) => {
  const filter = demos.find((item) => item.type === type);

  if (filter) {
    return filter.values
      .map(({ label, id }) => ({
        label,
        id,
        parent_id: type,
        type: 'demographic',
        class: type,
      }))
      .sort(alphabeticallySortByKey('label'));
  }

  return [];
};

export const getLevelWithAllOption = (item: LevelItem, pos: number) => {
  return {
    id: `all_${item.id}`,
    parent_id: item.id,
    label: `All ${item.label}`,
    type: 'category',
    pos,
  };
};

export const getDemographicValues = (
  demographicsItems: DemoItem[],
  isWwGwiMed: boolean,
  isGenericUs: boolean,
  isGenericGb: boolean
): { [key: string]: ReturnType<typeof mapDemoItems> } => {
  let keys = ['age', 'gender', 'generation'];

  if (isWwGwiMed) {
    keys = ['gender', 'country', 'age'];
  } else if (isGenericUs) {
    keys = [
      ...keys,
      'county_type',
      'education',
      'employment_status',
      'ethnicity',
      'has_children',
      'household_income',
      'job_function',
      'language',
      'metro_area',
      'race',
      'region',
      'relationship_status',
      'state',
    ];
  } else if (isGenericGb) {
    keys = [
      ...keys,
      'education',
      'BARB_regions',
      'employment_status',
      'area_of_employment',
      'ethnicity',
      'has_children',
      'relationship_status',
      'country',
      'social_classification',
    ];
  } else {
    keys = [...keys, 'country'];
  }

  return keys.reduce((acc, key) => {
    acc[key] = mapDemoItems(key, demographicsItems);
    return acc;
  }, {});
};

export const getLevelWithFusionPsychographic = (
  level,
  index,
  hasMriSimmonsFusion,
  hasGwiFusion,
  hasWWGwiMediaFusion,
  hasTgiFusion,
  hasDiscoverTaxonomy,
  isGenericGb
) => {
  const newLevel = [...level];
  if (
    hasMriSimmonsFusion ||
    hasGwiFusion ||
    hasWWGwiMediaFusion ||
    hasTgiFusion
  ) {
    if (index === 1) {
      newLevel.push({
        id: 'twitter',
        label: 'Discover',
        parent_id: 'psychographic',
        isSummary: false,
        isNotSelectable: true,
        description: 'Psychographics > Discover',
      });
    }
    newLevel.forEach((l) => {
      if (l.id === 'lifestyle' || l.id === 'personalities') {
        l.parent_id = 'twitter';
        l.description =
          l.lineage &&
          `Psychographics > Discover > ${l.lineage.substring(
            l.lineage.indexOf('>') + 1,
            l.lineage.length
          )}`;
      }

      if (l.parent_id === 'lifestyle' || l.parent_id === 'personalities') {
        l.description =
          l.lineage &&
          `Psychographics > Discover > ${l.lineage.substring(
            l.lineage.indexOf('>') + 1,
            l.lineage.length
          )}`;
      }
    });
  }
  if (!(hasDiscoverTaxonomy || isGenericGb)) {
    newLevel.forEach((l) => {
      if (l.parent_id === 'lifestyle') {
        l.description =
          l.lineage &&
          `Lifestyles > ${l.lineage.substring(
            l.lineage.lastIndexOf('>') + 1,
            l.lineage.length
          )}`;
      }
    });
  }
  return newLevel;
};

export const getBaseTaxonomy = ({
  taxonomy,
  demographicsItems,
  isGenericGb,
  isWwGwiMed,
  isGenericUs,
  hasUsPopulation21Plus,
  hasMriSimmonsFusion,
  hasGwiFusion,
  hasWWGwiMediaFusion,
  hasTgiFusion,
  hasDiscoverTaxonomy,
}: {
  taxonomy: LevelItem[][];
  demographicsItems: DemoItem[];
  isGenericGb: boolean;
  isWwGwiMed: boolean;
  isGenericUs: boolean;
  hasUsPopulation21Plus: boolean;
  hasMriSimmonsFusion: boolean;
  hasGwiFusion: boolean;
  hasWWGwiMediaFusion: boolean;
  hasTgiFusion: boolean;
  hasDiscoverTaxonomy: boolean;
}): LevelItem[][] => {
  if (taxonomy.length === 0) {
    return [];
  }

  const demographicValues = getDemographicValues(
    demographicsItems,
    isWwGwiMed,
    isGenericUs,
    isGenericGb
  );

  const toAdd = [];
  const result = taxonomy.map((level, index) => {
    level.forEach((item) => {
      if (!itemsWithoutAllOption.includes(item.id) && item.has_children) {
        toAdd.push(getLevelWithAllOption(item, index + 1));
      }
    });
    if (index === 0) {
      const topLevel =
        isGenericGb || hasDiscoverTaxonomy
          ? level
          : replacePsycoWithLifestyle(level, {
              label: 'Lifestyle',
              psychographicType: 'psychographic',
              omitDescription: true,
            });

      if (hasUsPopulation21Plus) {
        return [
          {
            id: US_POPULATION_21_PLUS_ID,
            label: SCOPE_LABELS.US_POPULATION_21_PLUS,
            type: 'membership',
            class: 'custom_base',
            parent_id: null,
            has_children: false,
          },
          ...topLevel,
        ];
      }

      return topLevel;
    }

    if (index === 1) {
      let result = [...taxonomy[1].sort(alphabeticallySortByKey('label'))];

      result.forEach((item) => {
        if (item.id === 'has_children') {
          item.id = 'presence_of_children';
        }
      });
      Object.keys(demographicValues).forEach((key) => {
        if (key === 'uk_country') {
          const childrenValues = demographicValues[key];
          childrenValues.forEach((item) => {
            if (item.parent_id === 'uk_country') {
              item.parent_id = 'country';
            }
          });
          result = [...result, ...childrenValues];
        } else if (key === 'uk_ethnicity') {
          const childrenValues = demographicValues[key];
          childrenValues.forEach((item) => {
            if (item.parent_id === 'uk_ethnicity') {
              item.parent_id = 'ethnicity';
            }
          });
          result = [...result, ...childrenValues];
        } else if (key === 'has_children') {
          const childrenValues = demographicValues[key];
          childrenValues.forEach((item) => {
            if (item.parent_id === 'has_children') {
              item.parent_id = 'presence_of_children';
            }
          });
          result = [...result, ...childrenValues];
        } else {
          result = [...result, ...demographicValues[key]];
        }
      });

      result = getLevelWithFusionPsychographic(
        result,
        index,
        hasMriSimmonsFusion,
        hasGwiFusion,
        hasWWGwiMediaFusion,
        hasTgiFusion,
        hasDiscoverTaxonomy,
        isGenericGb
      );
      return result;
    }

    return level.sort(alphabeticallySortByKey('label'));
  });

  const mappedResult = result.map((level, index) => {
    const toAddItems = toAdd.filter((item) => item.pos === index);
    return [...toAddItems, ...level];
  });
  return mappedResult;
};
